import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Button, FileUpload, Icon } from '@ibk/shared-ui';
import styles from '../SpreadsheetImportModal.module.css';
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_KB, UploadErrorType } from '../types';
import { generateTemplate } from '../utils/templateGenerator';
export function UploadView({ onFileSelected, onShowInstructions, onUploadError, inlineError }) {
    const { t } = useTranslation();
    const [fileError, setFileError] = useState('');
    const handleFileChange = useCallback((file) => {
        if (!file) {
            onFileSelected(null);
            return;
        }
        const fileSizeKB = file.size / 1024;
        if (fileSizeKB > MAX_FILE_SIZE_KB) {
            onUploadError(UploadErrorType.FILE_SIZE);
            return;
        }
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!fileExtension || !ACCEPTED_FILE_TYPES.includes(fileExtension)) {
            onUploadError(UploadErrorType.INVALID_FORMAT);
            return;
        }
        onFileSelected(file);
    }, [onFileSelected, onUploadError]);
    const handleDownloadTemplate = useCallback(() => {
        generateTemplate();
    }, []);
    const getInlineErrorMessage = () => {
        if (!inlineError)
            return null;
        if (inlineError === UploadErrorType.FILE_SIZE)
            return t('spreadsheet.import.error.file-size');
        if (inlineError === UploadErrorType.INVALID_FORMAT)
            return t('spreadsheet.import.error.invalid-format');
        return null;
    };
    const inlineErrorMessage = getInlineErrorMessage();
    const hasFileError = Boolean(fileError) || Boolean(inlineErrorMessage);
    const uploadContainerClassName = hasFileError
        ? `${styles.uploadContainer} ${styles.uploadContainerError}`
        : styles.uploadContainer;
    return (_jsxs("div", { className: uploadContainerClassName, "data-testid": 'viewSpreadsheetImportUpload', children: [_jsx("button", { type: 'button', className: styles.linkButton, onClick: onShowInstructions, "data-testid": 'buttonSpreadsheetImportShowInstructions', children: t('spreadsheet.import.upload.show-instructions') }), _jsx(FileUpload, { acceptTypes: ACCEPTED_FILE_TYPES, maxSizeKB: MAX_FILE_SIZE_KB, onChange: handleFileChange, error: fileError, setError: setFileError, dataTestId: 'fileUploadSpreadsheetImport' }), inlineErrorMessage && (_jsxs("div", { className: styles.uploadInlineError, "data-testid": 'textSpreadsheetImportInlineError', children: [_jsx(Icon, { name: 'circleError', variant: 'default', width: 16, height: 16 }), _jsx("span", { children: inlineErrorMessage })] })), _jsxs("div", { className: styles.uploadFooter, children: [_jsxs("button", { type: 'button', className: styles.linkButtonMd, onClick: handleDownloadTemplate, "data-testid": 'buttonSpreadsheetImportDownloadTemplate', children: [_jsx(Icon, { name: 'download', variant: 'default', width: 16, height: 16 }), t('spreadsheet.import.upload.download-template')] }), _jsx(Button, { variant: 'primary', size: 'medium', dataTestId: 'buttonSpreadsheetImportImportDisabled', disabled: true, children: t('spreadsheet.import.file-loaded.import-button') })] })] }));
}
